import { useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { useQuery } from "@/hooks/useApi";
import { approvalRequests, type ApprovalStatistics } from "@/api/approvalRequests.service";
import { pipelineStatistics, type PipelineStatistics } from "@/api/pipelineStatistics.service";
import LoadingState from "@/components/LoadingState";
import styles from "./Statistics.module.css";

type Variant = "success" | "danger" | "warning" | "accent" | "muted";

type StatCardProps = {
  label: string;
  value: number;
  total?: number;
  variant?: Variant;
};

function pct(value: number, total: number): number | null {
  if (total === 0) return null;
  return Math.round((value / total) * 100);
}

function StatCard({ label, value, total, variant }: StatCardProps) {
  const percentage = total !== undefined ? pct(value, total) : null;

  return (
    <div className={styles.statCard}>
      <p className={styles.statLabel}>{label}</p>
      <div className={styles.statValueRow}>
        <p className={styles.statValue}>{value.toLocaleString()}</p>
        {percentage !== null && variant && (
          <span className={`${styles.statPct} ${styles[variant]}`}>{percentage}%</span>
        )}
      </div>
      {percentage !== null && variant && (
        <div className={styles.statBar}>
          <div
            className={`${styles.statBarFill} ${styles[variant]}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}
    </div>
  );
}

function PipelineFailuresSection({ stats }: { stats: PipelineStatistics }) {
  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Pipeline Failures</h2>
      <div className={styles.statGrid}>
        <StatCard label="Enrichment failed" value={stats.enrichmentFailed} variant="danger" />
        <StatCard
          label="Send to approval failed"
          value={stats.sendToApprovalFailed}
          variant="danger"
        />
        <StatCard label="Push to CMS failed" value={stats.pushFailed} variant="danger" />
        <StatCard
          label="Items without research ids"
          value={stats.missingNewAxisIds}
          variant="warning"
        />
      </div>
    </div>
  );
}

function HumanReviewSection({ stats }: { stats: ApprovalStatistics }) {
  return (
    <>
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Human Review</h2>
        <div className={styles.statGrid}>
          <StatCard label="Total received" value={stats.total} />
          <StatCard label="Approved" value={stats.approved} total={stats.total} variant="success" />
          <StatCard label="Rejected" value={stats.rejected} total={stats.total} variant="danger" />
          <StatCard
            label="Pending (no decision yet)"
            value={stats.pending}
            total={stats.total}
            variant="warning"
          />
          <StatCard label="Suspended" value={stats.suspended} total={stats.total} variant="muted" />
        </div>
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Approved — Breakdown</h2>
        <div className={styles.statGrid}>
          <StatCard
            label="Accepted without changes"
            value={stats.approvedWithoutChanges}
            total={stats.approved}
            variant="success"
          />
          <StatCard
            label="Accepted with user edits"
            value={stats.approvedWithChanges}
            total={stats.approved}
            variant="accent"
          />
        </div>
      </div>
    </>
  );
}

function StatisticsPage() {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const toLocalDate = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const firstOfMonth = toLocalDate(new Date(now.getFullYear(), now.getMonth(), 1));
  const lastOfMonth = toLocalDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));

  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(lastOfMonth);

  const filters = {
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  };

  const pipeline = useQuery(() => pipelineStatistics.get(filters), [dateFrom, dateTo]);
  const humanReview = useQuery(() => approvalRequests.getStatistics(filters), [dateFrom, dateTo]);

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Statistics" subtitle="Testimonial review statistics" />

      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label htmlFor="dateFrom">From</label>
          <input
            id="dateFrom"
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
          />
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="dateTo">To</label>
          <input id="dateTo" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
      </div>

      {pipeline.loading ? (
        <LoadingState />
      ) : pipeline.error ? (
        <p className={styles.error}>Failed to load pipeline statistics: {pipeline.error.message}</p>
      ) : (
        pipeline.data && <PipelineFailuresSection stats={pipeline.data.statistics} />
      )}

      {humanReview.loading ? (
        <LoadingState />
      ) : humanReview.error ? (
        <p className={styles.error}>
          Failed to load review statistics: {humanReview.error.message}
        </p>
      ) : (
        humanReview.data && <HumanReviewSection stats={humanReview.data.statistics} />
      )}
    </div>
  );
}

export default StatisticsPage;
